/**
 * Minimal open/close context for the global Command Palette.
 *
 * Kept intentionally small so any layout component (header search trigger,
 * status bar Cmd+K hint, project viewer tabs) can imperatively `open()`
 * the palette without owning state.
 *
 * The full command-registry, list rendering, and keyboard handling live
 * in `CommandPalette.tsx` + `CommandRegistry.ts`. This file only exposes
 * the `open()` / `close()` / `isOpen` triplet.
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'

interface CommandPaletteContextValue {
  isOpen: boolean
  open: () => void
  close: () => void
  toggle: () => void
}

const defaultValue: CommandPaletteContextValue = {
  isOpen: false,
  open: () => {
    /* no-op until provider mounts */
  },
  close: () => {
    /* no-op */
  },
  toggle: () => {
    /* no-op */
  },
}

const CommandPaletteContext = createContext<CommandPaletteContextValue>(defaultValue)

interface ProviderProps {
  children: React.ReactNode
}

export const CommandPaletteProvider = ({ children }: ProviderProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false)
  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])
  const toggle = useCallback(() => setIsOpen((v) => !v), [])

  const value = useMemo(
    () => ({ isOpen, open, close, toggle }),
    [isOpen, open, close, toggle],
  )

  return (
    <CommandPaletteContext.Provider value={value}>{children}</CommandPaletteContext.Provider>
  )
}

export const useCommandPalette = (): CommandPaletteContextValue =>
  useContext(CommandPaletteContext)
